import { getLessonComponents } from './functions/getLessonComponents';
import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import styles from './Homework.module.scss';
import { Form, ModalContent } from './components/ModalContent';
import { _notify, NotificationTypes } from 'src/utils/notify';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { LegacyModal } from 'src/components/Modal/LegacyModal';
import { SelectItem } from 'primereact/selectitem';
import { Card } from 'src/components/Card/Card';
import { classNames } from 'primereact/utils';
import { Helmet } from 'react-helmet-async';
import Loader from 'src/components/Loader';
import { Toast } from 'primereact/toast';
import { map } from 'lodash';
import { checkHomework } from 'src/store/admin/homework/homework.actions';
import { getHomeworkCardComponents } from './functions/getHomeworkCardComponents';
import {
    useGetTeacherGroupsQuery,
    useGetTeacherSubjectsQuery,
} from 'src/api/endpoints/group/teacher/api';
import { GroupShort } from 'src/@types/modules/groups/groups.types';
import { skipToken } from '@reduxjs/toolkit/query';
import { useWhoamiQuery } from 'src/api/endpoints/auth/api';
import { SubjectShort } from 'src/@types/modules/subjects/subjects.types';
import { useNumberParam } from 'src/hooks/params';
import { Nullable } from 'primereact/ts-helpers';
import {
    GroupMaterial,
    useGetGroupMaterialsQuery,
} from 'src/api/endpoints/group-materials/api';
import {
    HomeworkSolution,
    useGetHomeworkSolutionsQuery,
} from 'src/api/endpoints/homeworks/api';
import { LessonBase } from 'src/@types/modules/lessons/lessons.types';
import { formatMaterialName } from 'src/api/endpoints/materials';
import { DownloadMaterialFilesButton } from 'src/pages/Materials';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';

export const HomeworkPage: FC<AuthorizedUserProps> = (props: AuthorizedUserProps): ReactElement => {
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();

    const [modal, setModal] = useState(false);

    const [homework, setHomework] = useState<HomeworkSolution | null>(null);

    const { data: whoami } = useWhoamiQuery();

    // region Teacher Groups
    const [teacherGroups, setTeacherGroups] = useState<GroupShort[]>([]);

    const [selectedGroup, selectGroup] = useState<GroupShort | null>(null);
    const [selectedGroupId, selectGroupId] = useNumberParam<GroupShort>({
        fieldName: 'groupId',
        ref: selectedGroup,
    });

    const {
        data: teacherGroupsResponse,
        isLoading: isTeacherGroupsResponseIsLoading,
        error: teacherGroupsResponseError,
    } = useGetTeacherGroupsQuery(
        whoami
            ? {
                  params: {
                      branchId: whoami.defaultBranchId,
                      teacherId: whoami.id,
                  },
              }
            : skipToken
    );

    useEffect((): void => {
        if (selectedGroupId === undefined) return;
        if (!teacherGroupsResponse || isTeacherGroupsResponseIsLoading) return;

        const foundedGroup: Nullable<GroupShort> = teacherGroupsResponse.find(
            (group: GroupShort): boolean => group.id === selectedGroupId
        );

        if (!foundedGroup) return selectGroupId(null);

        selectGroup(foundedGroup);
    }, [
        isTeacherGroupsResponseIsLoading,
        teacherGroupsResponse,
        selectedGroupId,
    ]);

    useEffect((): void => {
        if (isTeacherGroupsResponseIsLoading) return;
        if (!teacherGroupsResponse) return;

        setTeacherGroups(teacherGroupsResponse);
    }, [isTeacherGroupsResponseIsLoading, teacherGroupsResponse]);

    useEffect((): void => {
        if (isTeacherGroupsResponseIsLoading) return;
        if (!teacherGroupsResponseError) return;

        _notify(toast, {
            type: NotificationTypes.Exception,
            content: 'Не получилось получить группы',
        });
    }, [isTeacherGroupsResponseIsLoading, teacherGroupsResponseError]);
    // endregion

    // region Teacher Group Subject
    const [teacherGroupSubjects, setTeacherGroupSubjects] = useState<
        SubjectShort[]
    >([]);

    const [selectedSubject, selectSubject] = useState<SubjectShort | null>(
        null
    );
    const [selectedSubjectId, selectSubjectId] = useNumberParam<SubjectShort>({
        fieldName: 'subjectId',
        ref: selectedSubject,
    });

    const {
        data: teacherGroupSubjectsResponse,
        isLoading: isTeacherGroupSubjectsResponseIsLoading,
        error: teacherGroupSubjectsResponseError,
    } = useGetTeacherSubjectsQuery(
        selectedGroupId && whoami
            ? {
                  params: {
                      branchId: whoami.defaultBranchId,
                      teacherId: whoami.id,
                  },
                  search: { groupId: selectedGroupId },
              }
            : skipToken
    );

    useEffect((): void => {
        if (selectedSubjectId === undefined) return;
        if (
            !teacherGroupSubjectsResponse ||
            isTeacherGroupSubjectsResponseIsLoading
        )
            return;

        const foundedSubject: Nullable<SubjectShort> =
            teacherGroupSubjectsResponse.find(
                (subject: SubjectShort): boolean =>
                    subject.id === selectedSubjectId
            );

        if (!foundedSubject) return selectSubjectId(null);

        selectSubject(foundedSubject);
    }, [
        isTeacherGroupSubjectsResponseIsLoading,
        teacherGroupSubjectsResponse,
        selectedSubjectId,
    ]);

    useEffect((): void => {
        if (isTeacherGroupSubjectsResponseIsLoading) return;
        if (!teacherGroupSubjectsResponse) return;

        setTeacherGroupSubjects(teacherGroupSubjectsResponse);
    }, [isTeacherGroupSubjectsResponseIsLoading, teacherGroupSubjectsResponse]);

    useEffect((): void => {
        if (isTeacherGroupSubjectsResponseIsLoading) return;
        if (!teacherGroupSubjectsResponseError) return;

        _notify(toast, {
            type: NotificationTypes.Exception,
            content: 'Не получилось получить предметы',
        });
    }, [
        isTeacherGroupSubjectsResponseIsLoading,
        teacherGroupSubjectsResponseError,
    ]);
    // endregion

    // region Opened Group Materials
    const [groupMaterials, setGroupMaterials] = useState<GroupMaterial[]>([]);

    const [selectedMaterial, selectMaterial] = useState<GroupMaterial | null>(
        null
    );
    const [selectedMaterialId, selectMaterialId] =
        useNumberParam<GroupMaterial>({
            fieldName: 'materialId',
            ref: selectedMaterial,
        });

    const {
        data: groupMaterialsResponse,
        isLoading: isGroupMaterialsResponseLoading,
        error: groupMaterialsResponseError,
    } = useGetGroupMaterialsQuery(
        selectedGroupId && selectedSubjectId
            ? {
                  params: {
                      groupId: selectedGroupId,
                      subjectId: selectedSubjectId,
                  },
              }
            : skipToken
    );

    useEffect((): void => {
        if (selectedMaterialId === undefined) return;
        if (!groupMaterialsResponse || isGroupMaterialsResponseLoading) return;

        const foundedMaterial: Nullable<GroupMaterial> =
            groupMaterialsResponse.find(
                (material: GroupMaterial): boolean =>
                    material.id === selectedMaterialId
            );

        if (!foundedMaterial) return selectMaterialId(null);

        selectMaterial(foundedMaterial);
    }, [
        isGroupMaterialsResponseLoading,
        groupMaterialsResponse,
        selectedMaterialId,
    ]);

    useEffect((): void => {
        if (isGroupMaterialsResponseLoading) return;
        if (!groupMaterialsResponse) return;

        setGroupMaterials(groupMaterialsResponse);
    }, [isGroupMaterialsResponseLoading, groupMaterialsResponse]);

    useEffect((): void => {
        if (isGroupMaterialsResponseLoading) return;
        if (!groupMaterialsResponseError) return;

        _notify(toast, {
            type: NotificationTypes.Exception,
            content: 'Не получилось получить информацию о материалах',
        });
    }, [isGroupMaterialsResponseLoading, groupMaterialsResponse]);
    // endregion

    const { data: solutions } = useGetHomeworkSolutionsQuery(
        selectedGroupId && selectedMaterialId
            ? {
                  params: {
                      groupId: selectedGroupId,
                      materialId: selectedMaterialId,
                  },
              }
            : skipToken
    );

    function onSubmit(form: Form): void {
        if (!homework || !selectedMaterial) return;

        dispatch(
            checkHomework({
                id: homework.id,
                mark: form.mark,
                comment: form.comment,
            })
        )
            .unwrap()
            .then((): void => {
                _notify(
                    toast,
                    {
                        type: NotificationTypes.Notification,
                        content: 'Задание успешно проверено!',
                    },
                    true
                );
                onHide();
            })
            .catch(e => {
                _notify(toast, {
                    type: NotificationTypes.Exception,
                    content: e,
                });
            });
    }

    function onHide() {
        setModal(false);
        setHomework(null);
    }

    return (
        <StudentLayout auth={props}>
            <Toast ref={toast} />
            <Helmet title={'Домашнее задание'} />
            <LegacyModal
                visible={modal}
                header={
                    homework?.status === 'Выполнено'
                        ? 'Просмотр домашнего задания'
                        : 'Проверка домашнего задания'
                }
                onHide={() => setModal(false)}
                className={styles.modal}
            >
                {homework ? (
                    <ModalContent homework={homework} onSubmit={onSubmit} />
                ) : null}
            </LegacyModal>
            <Card
                className={styles.page}
                contentClassName={styles.pageContent}
                headerClassName={styles.pageHeader}
                header={'Домашнее задание'}
            >
                <div className={'flex flex-wrap gap-24'}>
                    <Dropdown
                        filter
                        value={selectedGroupId}
                        options={map(
                            teacherGroups,
                            (group: GroupShort): SelectItem => ({
                                value: group.id,
                                label: group.name,
                            })
                        )}
                        placeholder={'Выберите группу'}
                        onChange={(event: DropdownChangeEvent): void => {
                            selectGroupId(event.value as number);
                            selectSubjectId(null);
                            selectMaterialId(null);
                            setGroupMaterials([]);
                        }}
                    />

                    {selectedGroupId ? (
                        <Dropdown
                            filter
                            value={selectedSubjectId}
                            options={map(
                                teacherGroupSubjects,
                                (group: SubjectShort): SelectItem => ({
                                    value: group.id,
                                    label: group.name,
                                })
                            )}
                            placeholder={'Выберите предмет'}
                            onChange={(event: DropdownChangeEvent): void => {
                                selectSubjectId(event.value as number);
                                selectMaterialId(null);
                                setGroupMaterials([]);
                            }}
                        />
                    ) : null}
                </div>

                {groupMaterials.length ? (
                    <>
                        <div className={classNames('mt-24', styles.header)}>
                            Урок №
                        </div>
                        <div
                            className={'flex flex-wrap'}
                            style={{ gap: 12, marginTop: 12 }}
                        >
                            {getLessonComponents<GroupMaterial & LessonBase>({
                                lessons: groupMaterials.map(
                                    (lesson, index) => ({
                                        ...lesson,
                                        index: index + 1,
                                    })
                                ),
                                isDisabled: (
                                    material: GroupMaterial
                                ): boolean => {
                                    return !material.hasHomework;
                                },
                                onLessonClick: (
                                    material: GroupMaterial
                                ): void => {
                                    selectMaterial(material);
                                },
                                isSelected: (
                                    material: GroupMaterial
                                ): boolean => {
                                    return material.id === selectedMaterial?.id;
                                },
                            })}
                        </div>
                    </>
                ) : null}

                {selectedMaterial ? (
                    <div
                        className={'material-info'}
                        style={{
                            display: 'flex',
                            justifyContent: 'start',
                            alignItems: 'start',
                            width: '100%',
                            flexDirection: 'column',
                            gap: '15px',
                        }}
                    >
                        <div className={classNames('mt-48', styles.header)}>
                            {formatMaterialName(selectedMaterial.material)}
                        </div>
                        <DownloadMaterialFilesButton
                            material={selectedMaterial.material}
                            segment={'homework'}
                            label={'Скачать задание'}
                        />
                    </div>
                ) : null}

                {selectedMaterial ? (
                    <>
                        <div className={classNames('mt-48', styles.header)}>
                            Студенты:
                        </div>
                        {!solutions ? (
                            <Loader />
                        ) : (
                            <div className={'grid'}>
                                {getHomeworkCardComponents({
                                    homeworks: solutions,
                                    onHomeworkClick: (
                                        solution: HomeworkSolution
                                    ): void => {
                                        setHomework(solution);
                                        setModal(true);
                                    },
                                })}
                            </div>
                        )}
                    </>
                ) : null}
            </Card>
        </StudentLayout>
    );
};
